import { useEffect, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { ComponentLoader } from '@snsw-gel/react';
import { AuthContext } from 'app/sharedServices/authentication/AuthContext';
import { isVerifiedCsr } from 'app/sharedServices/authentication/authentication';
import { getStorageItem, setStorageItem } from 'app/sharedServices/storage/storage';
import { getBookingEligibility } from 'app/services/api/internal/API';
import { extractDataOrOpenErrorModal } from 'app/services/api/dataExtraction';

interface BookingEligibilityGuardProps {
  children: React.ReactNode;
}

const BookingEligibilityGuard: React.FC<BookingEligibilityGuardProps> = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [blocked, setBlocked] = useState(false);
  const history = useHistory();
  const { checkIsAuthenticated } = useContext(AuthContext);

  useEffect(() => {
    let isMounted = true;
    const checkEligibility = async () => {
      const isAuthenticated = checkIsAuthenticated();
      const isAdminUser = isVerifiedCsr();

      if (!isAuthenticated || isAdminUser) {
        if (isMounted) {
          setChecking(false);
        }
        return;
      }

      const serviceId = getStorageItem('currentServiceInfo')?.id;

      // Skip if service info not available yet
      if (!serviceId) {
        if (isMounted) {
          setChecking(false);
        }
        return;
      }

      try {
        const response = await getBookingEligibility(serviceId);
        const data = extractDataOrOpenErrorModal(response);
        if (data !== 'error' && data.eligible === false) {
          const homePath = getStorageItem('homePath', 'session');

          // Determine which limit was breached. Unique (session) limit takes
          // precedence over the active booking limit when both are present.
          const breachedLimit =
            data.uniqueBookings && data.uniqueBookings.eligible === false
              ? { limitType: 'unique' as const, ...data.uniqueBookings }
              : data.activeBookings && data.activeBookings.eligible === false
                ? { limitType: 'active' as const, ...data.activeBookings }
                : { limitType: 'unique' as const, currentCount: data.currentCount, maxAllowed: data.maxAllowed };

          sessionStorage.setItem(
            'bookingLimitReached',
            JSON.stringify({
              eligible: false,
              limitType: breachedLimit.limitType,
              currentCount: breachedLimit.currentCount,
              maxAllowed: breachedLimit.maxAllowed,
            }),
          );
          if (homePath) {
            if (isMounted) {
              setBlocked(true);
            }
            history.replace(homePath);
          }
        } else {
          // Eligible for this booking journey. Record it so later steps
          // (e.g. review/confirmation, where a reserved booking already counts against the limit) don't run the check again.
          setStorageItem('isBookingEligibilityChecked', true);
        }
      } catch (e) {
        // If eligibility check fails, allow user to proceed
        console.error('BookingEligibilityGuard: Failed to check booking eligibility', e);
      }
      if (isMounted) {
        setChecking(false);
      }
    };

    void checkEligibility();
    return () => {
      isMounted = false;
    };
  }, [checkIsAuthenticated, history]);

  if (checking) {
    return <ComponentLoader label="Checking eligibility..." fullPage />;
  }

  if (blocked) {
    return null;
  }

  return <>{children}</>;
};

export default BookingEligibilityGuard;
